/**
 * useReorderTaskPhases - Reordena fases (colunas) do Kanban de Tarefas
 *
 * Espelha a arquitetura já validada em useReorderPipelinePhases (CRM),
 * porém escopada por categoria de tarefas.
 *
 * @version 1.0.0
 * RELEASE: 2026-08-06 v1.0.0 - Criação do hook (RPC reorder_task_phases)
 */
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { createLogger } from '@/lib/logger';
import { toast } from 'sonner';

// Logger para depuração
const logger = createLogger('ReorderTaskPhases');

// Estrutura de ordem enviada ao backend
export interface TaskPhaseOrder {
  phase_id: string;
  sort_order: number;
}

/**
 * Hook para persistir a nova ordem das fases de uma categoria de tarefas.
 * A validação de tenant/categoria acontece server-side na RPC.
 *
 * @param categoryId - ID da categoria ativa no Kanban
 */
export function useReorderTaskPhases(categoryId: string | null) {
  const { currentTenant } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (phaseOrders: TaskPhaseOrder[]) => {
      if (!categoryId) {
        throw new Error('Categoria não selecionada');
      }

      logger.debug('Reordenando fases de tarefas', { categoryId, phaseOrders });

      const { error } = await supabase.rpc('reorder_task_phases', {
        p_phase_orders: phaseOrders as unknown as Parameters<
          typeof supabase.rpc<'reorder_task_phases'>
        >[1]['p_phase_orders'],
        p_category_id: categoryId,
        p_tenant_hint: currentTenant?.id || null,
      });

      if (error) {
        logger.error('Erro ao reordenar fases de tarefas', { error: error.message });
        throw error;
      }

      logger.info('Fases de tarefas reordenadas com sucesso');
    },
    onSuccess: () => {
      // Atualiza listagens de fases e contagens do Kanban
      queryClient.invalidateQueries({ queryKey: ['task-phases'] });
      queryClient.invalidateQueries({ queryKey: ['task-kanban-counts'] });
    },
    onError: (error: Error) => {
      toast.error(`Erro ao reordenar fases: ${error.message}`);
    },
  });
}
